import { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Search, Download, CheckCircle, XCircle, Clock, ArrowUpRight, RefreshCw, Filter, Calendar, ChevronLeft, ChevronRight, SlidersHorizontal, ArrowDownWideNarrow, Wallet } from 'lucide-react'

// MUI components
import { CircularProgress, Tooltip, Chip } from "@mui/material"

// Redux imports
import { exchangeActions } from "../../../../redux/actions"
import {
  homeScreenStatSelect,
  exchangeSelect,
} from "../../../../redux/selectors"
import { notify, money, moneyStatus, formatDateTime, capitalizeFirstLetter } from "../../../../utility/Helper1"
import NoData from "../../../../utility/customComponents/NoData"
import WrapUpP2p from "../../p2p/layouts/WrapUpP2p"

const WalletHistory = () => {
  // Redux state
  const finance = useSelector(homeScreenStatSelect)
  const dispatch = useDispatch()
  const exchangeData = useSelector(exchangeSelect)

  // Local state
  const [searchTerm, setSearchTerm] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState("all")
  const [dateRange, setDateRange] = useState("all")
  const [sortDirection, setSortDirection] = useState("desc")
  const [currentPage, setCurrentPage] = useState(1)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const itemsPerPage = 10

  // Derived data
  const withdrawalHistory = exchangeData?.filter(el => Number(el.asset) == 2) || []

  // Filter history based on search term and status
  const filteredHistory = withdrawalHistory.filter(item => {
    const matchesSearch =
      item?.withdrawalColumn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item?.motherType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      moneyStatus()[item?.completed]?.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "completed" && item?.completed == 1) ||
      (statusFilter === "pending" && item?.completed == 0) ||
      (statusFilter === "rejected" && item?.completed == 2)

    // Date filtering could be implemented here if needed

    return matchesSearch && matchesStatus
  })

  // Sort the filtered history
  const sortedHistory = [...filteredHistory].sort((a, b) => {
    const timeA = Number(a.ghtimestamp2)
    const timeB = Number(b.ghtimestamp2)
    return sortDirection === "desc" ? timeB - timeA : timeA - timeB
  })

  // Pagination
  const totalPages = Math.ceil(sortedHistory.length / itemsPerPage)
  const paginatedHistory = sortedHistory.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  // Calculate summary statistics
  const totalWithdrawn = withdrawalHistory?.reduce(
    (sum, item) => sum + Number.parseFloat(item.lastgh || 0),
    0
  ) || 0

  const pendingCount = withdrawalHistory?.filter(item => item.completed == 0).length || 0
  const completedCount = withdrawalHistory?.filter(item => item.completed == 1).length || 0
  const rejectedCount = withdrawalHistory?.filter(item => item.pausegh == 1).length || 0

  // Handlers
  const loadTransactionHistory = () => {
    setIsLoading(true)
    dispatch(exchangeActions.onGetExchange("p2p/load/all", response => {
      setIsLoading(false)
    }))
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 1:
        return "#10B981" // Green
      case 2:
        return "#EF4444" // Red
      case 0:
      default:
        return "#F59E0B" // Yellow
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 1:
        return <CheckCircle className="h-5 w-5" style={{ color: "#10B981" }} />
      case 2:
        return <XCircle className="h-5 w-5" style={{ color: "#EF4444" }} />
      case 0:
      default:
        return <Clock className="h-5 w-5" style={{ color: "#F59E0B" }} />
    }
  }

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return
    setCurrentPage(page)
  }

  const toggleSortDirection = () => {
    setSortDirection(prev => prev === "desc" ? "asc" : "desc")
  }

  // Effects
  useEffect(() => {
    loadTransactionHistory()
  }, [])

  useEffect(() => {
    // Reset to first page when filters change
    setCurrentPage(1)
  }, [searchTerm, statusFilter, dateRange, sortDirection])

  return (<WrapUpP2p>
    <div className="min-h-screen bg-[#050520] bg-gradient-to-br from-[#050520] to-[#0F172A] text-white">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">
              Withdrawal History
            </h1>
            <p className="text-gray-300 mt-1">Track and manage your transaction history</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Withdrawn Card */}
          <div className="rounded-2xl overflow-hidden relative bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-sm border border-gray-700/30 shadow-[0_4px_20px_rgba(0,0,0,0.2)]">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 to-purple-500"></div>
            <div className="p-5">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-400 text-sm">Total Withdrawn</p>
                  <h3 className="text-2xl font-bold text-white mt-1">{money(totalWithdrawn)}</h3>
                </div>
                <div className="p-2 rounded-lg bg-gradient-to-r from-cyan-500/20 to-purple-500/20 shadow-[0_0_10px_rgba(6,182,212,0.2)]">
                  <Wallet className="h-5 w-5 text-cyan-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Completed Card */}
          <div className="rounded-2xl overflow-hidden relative bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-sm border border-gray-700/30 shadow-[0_4px_20px_rgba(0,0,0,0.2)]">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 to-emerald-500"></div>
            <div className="p-5">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-400 text-sm">Completed</p>
                  <h3 className="text-2xl font-bold text-white mt-1">{completedCount}</h3>
                </div>
                <div className="p-2 rounded-lg bg-gradient-to-r from-green-500/20 to-emerald-500/20 shadow-[0_0_10px_rgba(16,185,129,0.2)]">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Pending Card */}
          <div className="rounded-2xl overflow-hidden relative bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-sm border border-gray-700/30 shadow-[0_4px_20px_rgba(0,0,0,0.2)]">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-yellow-500"></div>
            <div className="p-5">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-400 text-sm">Pending</p>
                  <h3 className="text-2xl font-bold text-white mt-1">{pendingCount}</h3>
                </div>
                <div className="p-2 rounded-lg bg-gradient-to-r from-amber-500/20 to-yellow-500/20 shadow-[0_0_10px_rgba(245,158,11,0.2)]">
                  <Clock className="h-5 w-5 text-amber-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Rejected Card */}
          <div className="rounded-2xl overflow-hidden relative bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-sm border border-gray-700/30 shadow-[0_4px_20px_rgba(0,0,0,0.2)]">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 to-rose-500"></div>
            <div className="p-5">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-400 text-sm">Rejected</p>
                  <h3 className="text-2xl font-bold text-white mt-1">{rejectedCount}</h3>
                </div>
                <div className="p-2 rounded-lg bg-gradient-to-r from-red-500/20 to-rose-500/20 shadow-[0_0_10px_rgba(239,68,68,0.2)]">
                  <XCircle className="h-5 w-5 text-red-400" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-gray-900/90 to-gray-800/90 backdrop-blur-sm border border-gray-700/30 shadow-[0_4px_30px_rgba(0,0,0,0.2)]">
          {/* Search and Filters */}
          <div className="p-6 border-b border-gray-700/50">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-grow">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Search className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search transactions..."
                  className="block w-full pl-10 pr-4 py-3 text-white border-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 rounded-xl bg-gray-800/50 border border-gray-700/50 shadow-[0_0_10px_rgba(6,182,212,0.1)]"
                />
              </div>

              {/* Filter Button */}
              <div className="flex gap-3">
                <button
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  className="px-4 py-3 rounded-xl bg-gradient-to-r from-gray-800 to-gray-700 hover:from-gray-700 hover:to-gray-600 border border-gray-600/50 flex items-center gap-2 transition-all duration-300"
                >
                  <Filter className="h-5 w-5 text-gray-300" />
                  <span>Filters</span>
                </button>

                <button
                  onClick={toggleSortDirection}
                  className="px-4 py-3 rounded-xl bg-gradient-to-r from-gray-800 to-gray-700 hover:from-gray-700 hover:to-gray-600 border border-gray-600/50 flex items-center gap-2 transition-all duration-300"
                >
                  <ArrowDownWideNarrow className={`h-5 w-5 text-gray-300 transition-transform duration-300 ${sortDirection === "asc" ? "rotate-180" : ""}`} />
                  <span>{sortDirection === "desc" ? "Newest" : "Oldest"}</span>
                </button>
              </div>
            </div>

            {/* Expanded Filters */}
            {isFilterOpen && (
              <div className="mt-4 p-4 rounded-xl bg-gray-800/50 border border-gray-700/50 grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setStatusFilter("all")}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-300 ${statusFilter === "all"
                          ? "bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-white border border-cyan-500/30 shadow-[0_0_10px_rgba(6,182,212,0.2)]"
                          : "bg-gray-700/50 text-gray-300 border border-gray-600/30 hover:bg-gray-700"
                        }`}
                    >
                      All
                    </button>
                    <button
                      onClick={() => setStatusFilter("completed")}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-300 ${statusFilter === "completed"
                          ? "bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-400 border border-green-500/30 shadow-[0_0_10px_rgba(16,185,129,0.2)]"
                          : "bg-gray-700/50 text-gray-300 border border-gray-600/30 hover:bg-gray-700"
                        }`}
                    >
                      Completed
                    </button>
                    <button
                      onClick={() => setStatusFilter("pending")}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-300 ${statusFilter === "pending"
                          ? "bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-400 border border-amber-500/30 shadow-[0_0_10px_rgba(245,158,11,0.2)]"
                          : "bg-gray-700/50 text-gray-300 border border-gray-600/30 hover:bg-gray-700"
                        }`}
                    >
                      Pending
                    </button>
                    <button
                      onClick={() => setStatusFilter("rejected")}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-300 ${statusFilter === "rejected"
                          ? "bg-gradient-to-r from-red-500/20 to-rose-500/20 text-red-400 border border-red-500/30 shadow-[0_0_10px_rgba(239,68,68,0.2)]"
                          : "bg-gray-700/50 text-gray-300 border border-gray-600/30 hover:bg-gray-700"
                        }`}
                    >
                      Rejected
                    </button>
                  </div>
                </div>

                {/* Date Range Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Date Range</label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setDateRange("all")}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-300 ${dateRange === "all"
                          ? "bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-white border border-cyan-500/30 shadow-[0_0_10px_rgba(6,182,212,0.2)]"
                          : "bg-gray-700/50 text-gray-300 border border-gray-600/30 hover:bg-gray-700"
                        }`}
                    >
                      All Time
                    </button>
                    <button
                      onClick={() => setDateRange("month")}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-300 ${dateRange === "month"
                          ? "bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-white border border-cyan-500/30 shadow-[0_0_10px_rgba(6,182,212,0.2)]"
                          : "bg-gray-700/50 text-gray-300 border border-gray-600/30 hover:bg-gray-700"
                        }`}
                    >
                      This Month
                    </button>
                    <button
                      onClick={() => setDateRange("week")}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-300 ${dateRange === "week"
                          ? "bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-white border border-cyan-500/30 shadow-[0_0_10px_rgba(6,182,212,0.2)]"
                          : "bg-gray-700/50 text-gray-300 border border-gray-600/30 hover:bg-gray-700"
                        }`}
                    >
                      This Week
                    </button>
                  </div>
                </div>

                {/* Custom Date Range (placeholder) */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Custom Range</label>
                  <button
                    className="w-full px-4 py-2 rounded-lg bg-gray-700/50 text-gray-300 border border-gray-600/30 hover:bg-gray-700 flex items-center justify-center gap-2 transition-all duration-300"
                  >
                    <Calendar className="h-4 w-4" />
                    <span>Select Dates</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Transaction Table */}
          <div className="p-6">
            {isLoading ? (
              <div className="py-16 flex justify-center">
                <CircularProgress size={40} sx={{ color: '#3B82F6' }} />
              </div>
            ) : paginatedHistory.length > 0 ? (
              <div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700/50">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Transaction
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700/30">
                      {paginatedHistory.map((item, index) => {
                        const image = finance?.filter((filitem) => filitem.type === item.currency)[0]

                        return (
                          <tr
                            key={index}
                            className="hover:bg-gradient-to-r hover:from-gray-800/30 hover:to-gray-700/30 transition-colors duration-200"
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <div className="mr-3 flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-cyan-500/20 to-purple-500/20 shadow-[0_0_10px_rgba(6,182,212,0.15)]">
                                  <Download className="h-5 w-5 text-cyan-400" />
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-white">
                                    {capitalizeFirstLetter(item?.withdrawalColumn || "Withdrawal")}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    ID: {item.id || "N/A"}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-red-400">
                                {money(item?.lastgh, item?.motherType)}
                              </div>
                              <div className="text-xs text-gray-400">
                                {item?.motherType || item?.currency}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-white">
                                {formatDateTime(Number(item.ghtimestamp2)).split(' ')[0]}
                              </div>
                              <div className="text-xs text-gray-400">
                                {formatDateTime(Number(item.ghtimestamp2)).split(' ')[1]}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                                style={{
                                  backgroundColor: `${getStatusColor(item?.completed)}20`,
                                  color: getStatusColor(item?.completed),
                                  border: `1px solid ${getStatusColor(item?.completed)}40`,
                                }}
                              >
                                {getStatusIcon(item?.completed)}
                                <span className="ml-1.5">{moneyStatus()[item?.completed]}</span>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-6">
                    <div className="text-sm text-gray-400">
                      Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, sortedHistory.length)} of {sortedHistory.length} results
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`p-2 rounded-lg border ${currentPage === 1
                            ? "border-gray-700 text-gray-500 cursor-not-allowed"
                            : "border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white"
                          }`}
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>

                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        // Logic to show pages around current page
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }

                        return (
                          <button
                            key={i}
                            onClick={() => handlePageChange(pageNum)}
                            className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ${currentPage === pageNum
                                ? "bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-white border border-cyan-500/30 shadow-[0_0_10px_rgba(6,182,212,0.2)]"
                                : "text-gray-300 border border-gray-700 hover:bg-gray-700"
                              }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}

                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`p-2 rounded-lg border ${currentPage === totalPages
                            ? "border-gray-700 text-gray-500 cursor-not-allowed"
                            : "border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white"
                          }`}
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-16 flex flex-col items-center justify-center rounded-xl border border-gray-700/50 bg-gray-800/20">
                <NoData title="No transaction history available" />
                <p className="text-gray-400 mt-2">Your withdrawal history will appear here</p>
                <button
                  onClick={() => {
                    setSearchTerm("")
                    setStatusFilter("all")
                    setDateRange("all")
                  }}
                  className="mt-4 px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500 text-white text-sm font-medium transition-all duration-300"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  </WrapUpP2p>
  )
}

export default WalletHistory